type ScriptType = 'hall' | 'museum';

interface ScriptState {
  isLoading: boolean;
  isLoaded: boolean;
  loadPromise: Promise<void> | null;
}

class ThreeJSManager {
  private scriptStates: Record<ScriptType, ScriptState> = {
    hall: { isLoading: false, isLoaded: false, loadPromise: null },
    museum: { isLoading: false, isLoaded: false, loadPromise: null }
  };

  private currentType: ScriptType | null = null;
  private isCleaningUp = false;

  private waitForBody(): Promise<void> {
    return new Promise((resolve) => {
      if (document.body) {
        resolve();
        return;
      }

      const observer = new MutationObserver(() => {
        if (document.body) {
          observer.disconnect();
          resolve();
        }
      });

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    });
  }

  async loadScript(type: ScriptType): Promise<void> {
    while (this.isCleaningUp) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (this.currentType === type && this.scriptStates[type].isLoaded && window.three_app) {
      //console.log(`Script ${type} already loaded and active`);
      return Promise.resolve();
    }
      
    await this.cleanup();

    const scriptState = this.scriptStates[type];
    
    if (scriptState.isLoading) {
      //console.log(`Script ${type} is already loading, waiting...`);
      return scriptState.loadPromise!;
    }

    // console.log(`Loading script for ${type}...`);
    scriptState.isLoading = true;
    scriptState.loadPromise = new Promise<void>(async (resolve) => {
      try {
        await new Promise(resolve => setTimeout(resolve, 0));
        
        if (!document.body) {
          await this.waitForBody();
        }

        if (!document.body) {
          console.error('Document body not available after waiting');
          this.resetScriptState(scriptState);
          resolve();
          return;
        }

        const script = document.createElement('script');        
        script.src = type === 'hall' ? 'assets/index.-uWnZ6m6.js' : 'assets/index.CEw0MdK6.js';
        script.type = 'module';
        
        script.onload = async () => {
          //console.log(`${type} script loaded successfully`);
          
          let attempts = 0;
          while (!window.three_app?.start && attempts < 50) {
            await new Promise(r => setTimeout(r, 200));
            attempts++;
          }
          
          if (!window.three_app?.start) {
            console.error(`${type} script loaded but three_app is not properly initialized`);
            this.resetScriptState(scriptState);
            resolve();
            return;
          }

          scriptState.isLoaded = true;
          scriptState.isLoading = false;
          this.currentType = type;
          resolve();
        };
        
        script.onerror = (error) => {
          console.error(`Failed to load ${type} script:`, error);
          this.resetScriptState(scriptState);
          resolve();
        };

        if (document.body) {
          document.body.appendChild(script);
        } else {
          throw new Error('Document body not available for script append');
        }
      } catch (error) {
        console.error('Error during script loading:', error);
        this.resetScriptState(scriptState);
        resolve();
      }
    });

    return scriptState.loadPromise;
  }

  async cleanup(): Promise<void> {
    if (this.isCleaningUp) {
      // console.log('Cleanup already in progress, waiting...');
      return;
    }

    this.isCleaningUp = true;
    //console.log('Starting cleanup...');

    try {
      if (window.three_app) {
        if (typeof window.three_app.destroy === 'function') {
          try {
            await window.three_app.destroy();
          } catch (error) {
            console.error('Error during three_app destroy:', error);
          }
        }
        window.three_app = undefined;
      }

      await new Promise(resolve => setTimeout(resolve, 0));

      if (document.body) {
        const scripts = document.querySelectorAll('script[src*="index-"]');
        scripts.forEach(script => script.remove());
      }

      Object.keys(this.scriptStates).forEach(type => {
        const state = this.scriptStates[type as ScriptType];
        state.isLoaded = false;
        state.isLoading = false;
        state.loadPromise = null;
      });

      this.currentType = null;
      
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error during cleanup:', error);
    } finally {
      this.isCleaningUp = false;
    }
  }

  getCurrentType(): ScriptType | null {
    return this.currentType;
  }

  isScriptLoaded(type: ScriptType): boolean {
    return this.scriptStates[type].isLoaded && this.currentType === type && !!window.three_app;
  }

  private resetScriptState(scriptState: ScriptState): void {
    scriptState.isLoading = false;
    scriptState.isLoaded = false;
    this.currentType = null;
  }
}

export const threeJSManager = new ThreeJSManager(); 